/**
 * useEnsureActiveBusiness — auto-populate businessStore.activeBusinessSlug
 * after authentication.
 *
 * Closes the loop on the X-Business-Id interceptor. Without this hook,
 * Enterprise cashiers never have activeBusinessSlug set in the store,
 * the interceptor sends no X-Business-Id header, and businessMiddleware
 * returns 400 on every business-scoped endpoint.
 *
 * Self-contained: doesn't depend on `useMyBusinesses` signature. Hits
 * `/businesses/mine` directly via the shared axios client so it benefits
 * from auth + the same interceptor stack.
 *
 * Rules:
 *   • No user OR slug already set → no-op (hook safe to call always)
 *   • Exactly 1 business returned → set it (single-biz tenant OR cashier)
 *   • 2+ AND cashier → match by user.businessId from JWT
 *   • 2+ AND admin → leave null (admin picks via Sidebar dropdown)
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMyBusinesses } from "@/hooks/useBusinesses";
import api from "@/services/api";
import useAuthStore from "@/stores/authStore";
import useBusinessStore from "@/stores/businessStore";

interface BusinessMin {
  _id: string;
  name: string;
  slug: string;
  isDefault?: boolean;
  currency?: string | null;
  industry?: string | null;
}

export function useEnsureActiveBusiness() {
  const user = useAuthStore((s) => s.user);
  const activeSlug = useBusinessStore((s) => s.activeBusinessSlug);
  const setActiveBusinessSlug = useBusinessStore(
    (s) => s.setActiveBusinessSlug,
  );
  // setAvailableBusinesses may or may not exist on the store. Read safely.
  const setAvailable = useBusinessStore((s) => {
    const sAny = s as unknown as {
      setAvailableBusinesses?: (b: BusinessMin[]) => void;
    };
    return sAny.setAvailableBusinesses;
  });

  const { data: businesses, isSuccess } = useQuery<BusinessMin[]>({
    queryKey: ["businesses-mine-for-active", user?.id],
    queryFn: async () => {
      const res = await api.get<
        { success?: boolean; data?: BusinessMin[] } | BusinessMin[]
      >("/businesses/mine");
      const body = res.data;
      if (Array.isArray(body)) return body;
      if (body && typeof body === "object" && Array.isArray(body.data)) {
        return body.data;
      }
      return [];
    },
    enabled: Boolean(user) && !activeSlug,
    staleTime: 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (!user || activeSlug) return;
    if (!isSuccess || !Array.isArray(businesses)) return;

    // ── Case 1: exactly one business returned ──────────────────────
    if (businesses.length === 1) {
      const only = businesses[0];
      setActiveBusinessSlug(only.slug);
      if (typeof setAvailable === "function") setAvailable(businesses);
      return;
    }

    // ── Case 2: multiple businesses, cashier role ──────────────────
    if (businesses.length > 1 && user.role === "cashier") {
      const userBusinessId = (user as { businessId?: string }).businessId;
      if (userBusinessId) {
        const match = businesses.find(
          (b) => String(b._id) === String(userBusinessId),
        );
        if (match) {
          setActiveBusinessSlug(match.slug);
          if (typeof setAvailable === "function") setAvailable(businesses);
          return;
        }
      }
      console.warn(
        "[useEnsureActiveBusiness] cashier has no resolvable business assignment",
        {
          userId: user.id,
          businessId: userBusinessId,
          count: businesses.length,
        },
      );
      return;
    }

    // ── Case 3: multiple businesses, admin role ────────────────────
    // Don't auto-pick; just populate the available list so the Sidebar
    // dropdown is ready when admin lands.
    if (typeof setAvailable === "function" && businesses.length > 0) {
      setAvailable(businesses);
    }
  }, [
    user,
    activeSlug,
    businesses,
    isSuccess,
    setActiveBusinessSlug,
    setAvailable,
  ]);
}

/**
 * 12a: keep the store's business list in step with the server list, so a
 * change in Settings (the type of business, its words) shows at once —
 * the store used to be filled only at sign-in.
 */
export function useSyncBusinessList() {
  const user = useAuthStore((s) => s.user);
  const setAvailable = useBusinessStore((s) => s.setAvailableBusinesses);
  const { data } = useMyBusinesses({ enabled: Boolean(user) });
  useEffect(() => {
    if (user && Array.isArray(data) && data.length > 0) setAvailable(data);
  }, [user, data, setAvailable]);
}

export default useEnsureActiveBusiness;
